/**
 * MODAL: CREATE PROSPECT
 * Modal pour créer un nouveau prospect (soumission de formulaire + contact)
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { useForms } from '../../hooks/useForms';
import { useOrigins } from '../../hooks/useOrigins';
import {
  Form,
  FormInput,
  FormSelect,
  FormSelectWithCreate,
  FormTextarea,
  FormPhoneInput,
} from '../ui';
import { Modal, ModalFooter } from '../ui/modal';
import { CreateOriginModal } from './CreateOriginModal';

// Schéma de validation
const createProspectSchema = z.object({
  form_id: z.string().uuid('Veuillez sélectionner un formulaire'),
  first_name: z.string().min(1, 'Le prénom est requis').max(100, 'Le prénom est trop long'),
  last_name: z.string().min(1, 'Le nom est requis').max(100, 'Le nom est trop long'),
  email: z.string().email('Email invalide'),
  phone: z.string().optional(),
  origin_id: z.string().uuid().optional(),
  notes: z.string().optional(),
});

type CreateProspectInput = z.infer<typeof createProspectSchema>;

interface CreateProspectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    form_id: string;
    contact: {
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      origin_id?: string;
      notes?: string;
    };
  }) => Promise<void>;
}

export function CreateProspectModal({ isOpen, onClose, onSubmit }: CreateProspectModalProps) {
  const { forms, loading: formsLoading } = useForms();
  const { origins, createOrigin } = useOrigins();
  const [activeForms, setActiveForms] = useState<typeof forms>([]);
  const [isCreateOriginModalOpen, setIsCreateOriginModalOpen] = useState(false);

  const form = useForm<CreateProspectInput>({
    resolver: zodResolver(createProspectSchema),
    defaultValues: {
      form_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      origin_id: undefined,
      notes: '',
    },
  });

  // Filtrer les formulaires actifs
  useEffect(() => {
    if (forms) {
      setActiveForms(forms.filter((f) => f.is_active));
    }
  }, [forms]);

  function handleClose() {
    form.reset();
    onClose();
  }

  async function handleFormSubmit(data: CreateProspectInput) {
    const submitData = {
      form_id: data.form_id,
      contact: {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone,
        origin_id: data.origin_id,
        notes: data.notes,
      },
    };

    await onSubmit(submitData);
    handleClose();
  }

  // Options pour l'origine
  const originOptions = origins.map((origin) => ({
    value: origin.id,
    label: origin.label,
  }));

  // Options pour les formulaires
  const formOptions = activeForms.map((f) => ({
    value: f.id,
    label: `${f.name}${f.product?.name ? ` (${f.product.name})` : ''}`,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Créer un prospect"
      size="lg"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel="Créer"
          isSubmitting={form.formState.isSubmitting}
          formId="create-prospect-form"
        />
      }
    >
      {formsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
        </div>
      ) : (
        <Form {...form}>
          <form id="create-prospect-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
            <div className="flex flex-col gap-4">
              {/* Sélection du formulaire */}
              <FormSelect
                control={form.control}
                name="form_id"
                label="Formulaire"
                required
                options={formOptions}
                placeholder="Sélectionnez un formulaire..."
                disabled={formOptions.length === 0}
              />

              {formOptions.length === 0 && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md border border-amber-200">
                  ⚠️ Aucun formulaire actif disponible. Veuillez d'abord créer et activer un
                  formulaire.
                </p>
              )}

              <div className="border-t border-zinc-200 pt-4">
                <h3 className="text-sm font-medium text-zinc-900 mb-3">Informations du contact</h3>

                {/* Ligne 1 : Prénom et Nom */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <FormInput
                    control={form.control}
                    name="first_name"
                    label="Prénom"
                    required
                    placeholder="Ex: Léo"
                  />
                  <FormInput
                    control={form.control}
                    name="last_name"
                    label="Nom"
                    required
                    placeholder="Ex: Usurpi"
                  />
                </div>

                {/* Ligne 2 : Email et Téléphone */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <FormInput
                    control={form.control}
                    name="email"
                    label="Email"
                    type="email"
                    required
                    placeholder="Ex: leo.usurpi@email.com"
                  />
                  <FormPhoneInput
                    control={form.control}
                    name="phone"
                    label="Téléphone"
                    placeholder="6 12 34 56 78"
                  />
                </div>

                {/* Origine */}
                <div className="mb-4">
                  <FormSelectWithCreate
                    control={form.control}
                    name="origin_id"
                    label="Origine (canal d'acquisition)"
                    options={originOptions}
                    placeholder="Sélectionnez une origine..."
                    onCreateClick={() => setIsCreateOriginModalOpen(true)}
                    createButtonLabel="Nouvelle"
                  />
                </div>

                {/* Notes */}
                <FormTextarea
                  control={form.control}
                  name="notes"
                  label="Notes"
                  placeholder="Notes sur le prospect..."
                  rows={3}
                />
              </div>
            </div>
          </form>
        </Form>
      )}

      <CreateOriginModal
        isOpen={isCreateOriginModalOpen}
        onClose={() => setIsCreateOriginModalOpen(false)}
        onSubmit={async (data) => {
          const newOrigin = await createOrigin(data);
          if (newOrigin) {
            form.setValue('origin_id', newOrigin.id);
          }
        }}
      />
    </Modal>
  );
}
